import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import type { RootState } from "@/store/store";
import { store } from "@/store/store";
import { logout } from "@/store/slices/auth.slice";
import { getApiUrl, isDebugMode } from "@/config/env";

// Types for API responses
export interface ApiResponse<T = unknown> {
  data: T;
  message: string;
  valid: boolean;
  success?: boolean;
  status: number;
}

export interface PaginatedApiResponse<T = unknown> extends ApiResponse<T> {
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public response?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Get the API base URL - uses centralized env configuration
 */
function getApiBaseUrl(): string {
  return getApiUrl();
}

class UnifiedApiClient {
  private client: AxiosInstance;
  private getStore: (() => RootState) | null = null;
  private baseURL: string;
  private initialized = false;

  constructor() {
    // Use centralized env configuration
    this.baseURL = getApiUrl();

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      withCredentials: true, // Important for cookie handling
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    // Log the configuration for debugging if debug mode is enabled
    if (isDebugMode()) {
      console.log("[ApiClient] Initialized with:", {
        baseURL: this.baseURL,
        withCredentials: true,
      });
    }

    this.setupInterceptors();
  }

  /**
   * Ensure the client is using the correct base URL for the current environment
   * This is called lazily on first request when running in the browser
   */
  private ensureCorrectBaseUrl(): void {
    if (this.initialized) return;

    const correctBaseUrl = getApiBaseUrl();
    if (correctBaseUrl !== this.baseURL) {
      this.baseURL = correctBaseUrl;
      this.client.defaults.baseURL = correctBaseUrl;
      console.log("[ApiClient] Updated baseURL for localhost:", {
        baseURL: correctBaseUrl,
        isLocalhost: true,
      });
    }
    this.initialized = true;
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // For multipart/form-data, let axios set the content type
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }

        // Replace :storeId placeholder in URLs
        if (config.url && config.url.includes(":storeId")) {
          const currentStoreId = this.getCurrentStoreId();
          if (currentStoreId) {
            const originalUrl = config.url;
            config.url = config.url.replace(":storeId", currentStoreId);
            console.log(
              `[ApiClient] URL replacement: ${originalUrl} -> ${config.url}`,
            );
          } else {
            console.warn(
              "API call requires storeId but no current store is set",
              config.url,
            );
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        // Enhanced error logging for debugging authentication issues
        console.error("[ApiClient] API Error Details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          withCredentials: error.config?.withCredentials,
          message: error.response?.data?.message || error.message,
          headers: error.response?.headers,
        });

        const status = error.response?.status || 500;
        const code = error.response?.data?.code || "UNKNOWN_ERROR";
        const message =
          error.response?.data?.message || error.message || "An error occurred";

        // Handle 401 Unauthorized - logout and redirect to login
        // Avoid forcing a full-page navigation to `/auth/login` if we're
        // already on an auth route (to prevent redirect/reload loops).
        if (status === 401) {
          console.error(
            "[ApiClient] 401 Unauthorized - logging out and redirecting to login",
          );
          store.dispatch(logout());

          if (typeof window !== "undefined") {
            const path = window.location.pathname || "";
            // If we're already on an /auth route, don't reload the page.
            if (!path.startsWith("/auth")) {
              window.location.href = "/auth/login";
            } else {
              console.log(
                "[ApiClient] Skipping redirect to /auth/login because current path is an auth route",
              );
            }
          }
        }

        // Special handling for auth errors
        if (status === 401 || status === 403) {
          if (isDebugMode()) {
            console.error("[ApiClient] Authentication/Authorization error:", {
              status,
              message,
              url: error.config?.url,
            });
          }
        }

        throw new ApiError(status, code, message, error.response?.data);
      },
    );
  }

  // Set the Redux store getter function
  setStoreGetter(getStore: () => RootState) {
    this.getStore = getStore;
  }

  // Get current store ID from Redux state
  private getCurrentStoreId(): string | null {
    try {
      // Prefer Redux-backed currentStoreId when available
      if (this.getStore) {
        const state = this.getStore();
        const storeId = state.auth.currentStoreId;
        if (storeId) {
          console.log(`[ApiClient] getCurrentStoreId() (from Redux) -> ${storeId}`);
          return storeId;
        }
      }

      // Fallback: attempt to read from current URL path (/storeType/storeId/...)
      if (typeof window !== "undefined" && window.location && window.location.pathname) {
        const segments = window.location.pathname.split("/").filter(Boolean);
        if (segments.length >= 2) {
          const maybeStoreId = segments[1];
          // Basic validation for UUID-like storeId (or simple id)
          if (/^[a-z0-9-]{8,}$/i.test(maybeStoreId)) {
            console.log(`[ApiClient] getCurrentStoreId() (from URL) -> ${maybeStoreId}`);
            return maybeStoreId;
          }
        }
      }

      console.warn("[ApiClient] No currentStoreId available (Redux and URL fallback failed)");
      return null;
    } catch (error) {
      console.error("Error getting current store ID in ApiClient:", error);
      return null;
    }
  }

  /**
   * Unwraps common API envelopes: { message, valid, data }, including legacy PascalCase keys.
   * Prefer inner `data`/`Data` when present so callers receive the payload (not the envelope).
   */
  private unwrapResponseBody(raw: unknown): {
    payload: unknown;
    message: string;
    valid: boolean;
    success: boolean;
  } {
    if (raw === null || typeof raw !== "object") {
      return {
        payload: raw,
        message: "",
        valid: true,
        success: true,
      };
    }

    const o = raw as Record<string, unknown>;

    const message =
      typeof o.message === "string"
        ? o.message
        : typeof o.Message === "string"
          ? o.Message
          : "";

    const valid =
      typeof o.valid === "boolean"
        ? o.valid
        : typeof o.Valid === "boolean"
          ? o.Valid
          : true;

    const success =
      typeof o.success === "boolean"
        ? o.success
        : typeof o.Success === "boolean"
          ? o.Success
          : true;

    let payload: unknown = raw;
    if ("data" in o) {
      payload = o.data;
    } else if ("Data" in o) {
      payload = o.Data;
    }

    return { payload, message, valid, success };
  }

  // Generic request method
  private async request<T = unknown>(
    config: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    // Ensure we're using the correct base URL (handles localhost detection)
    this.ensureCorrectBaseUrl();

    try {
      const response = await this.client.request(config);
      const { payload, message, valid, success } = this.unwrapResponseBody(
        response.data,
      );

      return {
        data: payload as T,
        message,
        valid,
        success,
        status: response.status,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
    }
  }

  // HTTP methods - return full ApiResponse<T> so callers can inspect message/valid/status and nested data
  async get<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
  ): Promise<ApiResponse<T>> {
    const res = await this.request<T>({
      method: "GET",
      url,
      params,
    });
    return res as ApiResponse<T>;
  }

  async post<T = unknown>(
    url: string,
    data?: unknown,
  ): Promise<ApiResponse<T>> {
    const res = await this.request<T>({
      method: "POST",
      url,
      data,
    });
    return res as ApiResponse<T>;
  }

  async put<T = unknown>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const res = await this.request<T>({
      method: "PUT",
      url,
      data,
    });
    return res as ApiResponse<T>;
  }

  async patch<T = unknown>(
    url: string,
    data?: unknown,
  ): Promise<ApiResponse<T>> {
    const res = await this.request<T>({
      method: "PATCH",
      url,
      data,
    });
    return res as ApiResponse<T>;
  }

  // Allow optional params/data for DELETE calls (many services pass a body or params)
  async delete<T = unknown>(
    url: string,
    dataOrParams?: Record<string, unknown>,
  ): Promise<ApiResponse<T>> {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      url,
    };

    if (dataOrParams) {
      // Put as params for convenience; some callers pass body-like objects
      config.params = dataOrParams;
      // axios supports data with DELETE in some environments; include as well
      config.data = dataOrParams;
    }

    const res = await this.request<T>(config);
    return res as ApiResponse<T>;
  }

  // Paginated requests
  async getPaginated<T = unknown>(
    url: string,
    params?: Record<string, unknown>,
  ): Promise<PaginatedApiResponse<T[]>> {
    // Ensure we're using the correct base URL (handles localhost detection)
    this.ensureCorrectBaseUrl();

    try {
      const response = await this.client.request({
        method: "GET",
        url,
        params,
      });

      // Handle the API response structure directly
      const responseData = response.data;

      console.log("API Response in getPaginated:", responseData);

      const result = {
        data: responseData.data || [], // The actual data array
        message: responseData.message || "",
        valid: responseData.valid ?? true,
        success: responseData.success ?? true,
        status: response.status,
        pagination: {
          total: responseData.total || 0,
          page: responseData.page || 1,
          pageSize: responseData.page_size || 10,
          totalPages: responseData.total_pages || 1,
        },
      };

      console.log("Transformed getPaginated result:", result);

      return result;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
    }
  }

  // File upload method
  async uploadFile<T = unknown>(
    url: string,
    file: File,
    additionalData?: Record<string, unknown>,
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, String(value));
      });
    }

    const res = await this.request<T>({
      method: "POST",
      url,
      data: formData,
    });
    return res;
  }

  // Get raw axios instance for advanced use cases
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Create singleton instance
export const apiClient = new UnifiedApiClient();

// Initialize the API client with Redux store
export const initializeApiClient = (getStore: () => RootState) => {
  apiClient.setStoreGetter(getStore);
};

// Export default instance for convenience
export default apiClient;
